import { AgeRestriction } from '@diritto/common/content/constants';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodePublicRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { TrackerExtraInfo, TrackerPosition, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { EpisodeInfo } from 'app-components/viewer/EpisodeInfo';
import { EpisodeNavigationBar } from 'app-components/viewer/EpisodeNavigationBar';
import { HeaderNavBarHeight } from 'constants/layout';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import React, { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { ContentBodyPaddingContainer } from 'ui-components/layout/containers/ContentBodyCenterContainer';
import { BackButton, HomeButton, InfoButton } from 'ui-components/navigations/HeaderButtons';

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.Reply,
};

interface EpisodeReplyToolbarViewProps {
  children: React.ReactNode;
  content?: ContentPublicRelayDto;
  episode?: EpisodePublicRelayDto;
  isShow?: boolean;
  onBack?: () => void;
  className?: string;
}

export function EpisodeReplyLayout({
  content,
  episode,
  children,
  onBack,
  isShow = true,
  className = '',
}: EpisodeReplyToolbarViewProps) {
  const { tracker } = useTrackingState();
  const { includeAdult } = useAuthState();
  const [isShowEpisodeList, setIsShowEpisodeList] = useState(false);

  const handleDeActive = useCallback((e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setIsShowEpisodeList(false);
  }, []);

  useEffect(() => {
    setIsShowEpisodeList(false);
  }, [episode]);

  const toggleEpisodeList = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (isShowEpisodeList) {
        handleDeActive(e as unknown as MouseEvent<HTMLElement>);
      } else if (content && episode) {
        tracker.track(TrackingEventType.ClickEpisodeListButtonClick, {
          contentId: content.id,
          episodeId: episode.id,
          trackerExtraInfo,
        });
        setIsShowEpisodeList(prev => !prev);
      }
    },
    [content, episode, handleDeActive, isShowEpisodeList, tracker],
  );

  const handleClickEpisodeLink = useCallback(() => {
    setIsShowEpisodeList(false);
  }, []);

  const leftButtons = useMemo(() => [<BackButton key="back" onClick={onBack} />, <HomeButton key="home" />], [onBack]);
  const rightButtons = useMemo(
    () => [
      <InfoButton
        key="list"
        color="black"
        onClickCapture={toggleEpisodeList}
        disabled={content?.ageRestriction === AgeRestriction.Adult && !includeAdult}
      />,
    ],
    [content?.ageRestriction, includeAdult, toggleEpisodeList],
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleDeActive}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          handleDeActive(e as unknown as MouseEvent<HTMLElement>);
        }
      }}
      className={`absolute inset-0 w-full h-full ${isShow ? 'flex' : 'hidden'} flex-col bg-white ${className}`}>
      <nav
        className="w-full"
        style={{
          height: `${HeaderNavBarHeight}px`,
          borderBottom: '1px solid #D0D7DE',
        }}>
        <ContentBodyPaddingContainer>
          <EpisodeNavigationBar
            contentId={content?.id}
            contentTitle={content?.title}
            episodeTitle={episode?.title}
            leftButtons={leftButtons}
            rightButtons={rightButtons}
            trackerExtraInfo={trackerExtraInfo}
          />
        </ContentBodyPaddingContainer>
      </nav>
      {children}
      {content && (
        <EpisodeInfo
          contentId={content.id}
          contentTitle={content.title}
          isAdultContent={content.ageRestriction === AgeRestriction.Adult}
          selectedEpisodeId={episode?.id}
          isShow={isShowEpisodeList}
          trackerExtraInfo={trackerExtraInfo}
          replaceLink
          onClickEpisodeLink={handleClickEpisodeLink}
        />
      )}
    </div>
  );
}
